// sapi-admin/src/services/storeService.js
import { db } from '../firebase/config';
import { 
  collection, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  query, 
  where,
  orderBy,
  serverTimestamp,
  onSnapshot  // ✅ Ajout pour temps réel
} from 'firebase/firestore';

const STORES_COLLECTION = 'piStores';

/**
 * Récupère toutes les boutiques actives (temps réel)
 */
export const getStores = async () => {
  try {
    const storesQuery = query(
      collection(db, STORES_COLLECTION),
      where('isActive', '==', true),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(storesQuery);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error fetching stores:', error);
    throw error;
  }
};

/**
 * ✅ Récupère toutes les boutiques en TEMPS RÉEL (admin - y compris inactives)
 * Utilise onSnapshot pour les mises à jour automatiques
 */
export const listenAllStores = (onUpdate, onError) => {
  try {
    const q = query(collection(db, STORES_COLLECTION), orderBy('createdAt', 'desc'));
    
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const stores = snapshot.docs.map(doc => ({ 
          id: doc.id, 
          ...doc.data(),
          userId: doc.data().userId,
          userName: doc.data().userName,
        }));
        onUpdate(stores);
      },
      (error) => {
        console.error('❌ Real-time stores error:', error);
        if (onError) onError(error);
      }
    );
    
    return unsubscribe;
  } catch (error) {
    console.error('Error setting up stores listener:', error);
    if (onError) onError(error);
    return null;
  }
};

/**
 * Récupère toutes les boutiques (une fois)
 */
export const getAllStores = async () => {
  try {
    const snapshot = await getDocs(collection(db, STORES_COLLECTION));
    return snapshot.docs.map(doc => ({ 
      id: doc.id, 
      ...doc.data(),
      userId: doc.data().userId,
      userName: doc.data().userName,
    }));
  } catch (error) {
    console.error('Error getting stores:', error);
    throw error;
  }
};

/**
 * Récupère une boutique par ID
 */
export const getStoreById = async (storeId) => {
  try {
    const docRef = doc(db, STORES_COLLECTION, storeId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    }
    return null;
  } catch (error) {
    console.error('Error fetching store:', error);
    throw error;
  }
};

/**
 * Ajoute une boutique
 */
export const addStore = async (storeData) => {
  try {
    const newStore = {
      ...storeData,
      isActive: true,
      isVerified: false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      paymentStatus: 'pending',
      paymentAmount: 0.5
    };
    const docRef = await addDoc(collection(db, STORES_COLLECTION), newStore);
    return { id: docRef.id, ...newStore };
  } catch (error) {
    console.error('Error adding store:', error);
    throw error;
  }
};

/**
 * Met à jour une boutique
 */
export const updateStore = async (storeId, storeData) => {
  try {
    const docRef = doc(db, STORES_COLLECTION, storeId);
    await updateDoc(docRef, {
      ...storeData,
      updatedAt: serverTimestamp()
    });
    return { id: storeId, ...storeData };
  } catch (error) {
    console.error('Error updating store:', error);
    throw error;
  }
};

/**
 * Supprime définitivement une boutique
 */
export const deleteStore = async (storeId) => {
  try {
    const storeRef = doc(db, STORES_COLLECTION, storeId);
    const storeSnap = await getDoc(storeRef);
    
    if (!storeSnap.exists()) {
      throw new Error('Store not found');
    }
    
    await deleteDoc(storeRef);
    console.log(`✅ Store ${storeId} definitively deleted`);
    return true;
  } catch (error) {
    console.error('Error deleting store:', error);
    throw error;
  }
};

// sapi-admin/src/services/storeService.js

/**
 * Active/Désactive une boutique (avec validation)
 */
export const toggleStoreStatus = async (storeId, isActive) => {
  try {
    const docRef = doc(db, STORES_COLLECTION, storeId);
    const storeSnap = await getDoc(docRef);
    
    if (!storeSnap.exists()) {
      throw new Error('Store not found');
    }
    
    const storeData = storeSnap.data();
    
    // ✅ VÉRIFICATION : Si on essaie d'activer un store non vérifié
    if (isActive && !storeData.isVerified) {
      throw new Error('Cannot activate a store that is not verified. Please verify the store first.');
    }
    
    await updateDoc(docRef, { 
      isActive: isActive, 
      updatedAt: serverTimestamp() 
    });
  } catch (error) {
    console.error('Error toggling store status:', error);
    throw error;
  }
};

/**
 * Valide une boutique (admin)
 */
export const verifyStore = async (storeId) => {
  try {
    const docRef = doc(db, STORES_COLLECTION, storeId);
    await updateDoc(docRef, { 
      isVerified: true, 
      verifiedAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error verifying store:', error);
    throw error;
  }
};

/**
 * Récupère le userId d'une boutique pour les notifications
 */
export const getStoreOwnerId = async (storeId) => {
  try {
    const docRef = doc(db, STORES_COLLECTION, storeId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return docSnap.data().userId;
    }
    return null;
  } catch (error) {
    console.error('Error getting store owner:', error);
    return null;
  }
};